import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { BUILD_SHA, isStale } from '../../lib/build.js';
import { useI18n } from '../../i18n/index.js';

// "I deployed it but I can't see that anything changed."
//
// The system had no answer to that from the inside. A deployed change reaches
// a browser only when the browser asks for the page again — index.html is
// no-cache, so a reload is enough — but a tab that has been open since
// yesterday keeps running the JavaScript it loaded yesterday, and nothing on
// screen says so. On a station PC that tab can be open for a week.
//
// So the bundle carries the commit it was built from, the server reports the
// commit it is running, and this compares them. When they differ it says so.
//
// It reloads by itself only when nothing can be lost (see safeToReload below):
// a kiosk that reloads under a worker's hands mid-scan loses whatever they were doing.
const POLL_MS = 60_000;
//
// ⚠ אלי 05/11/2026: „ביקשתי שתעשה רענון" — הטלפון הריץ גרסה ישנה של חלון ההזמנה
// (מחיר אחד לכל הפריטים) למרות שהתיקון היה באוויר. ולכן כן מרעננים לבד — אבל רק
// כשזה לא יכול לאבד עבודה: אין חלון פתוח, אף שדה לא בפוקוס, ואין שום פעולה
// (מקש / לחיצה / גלילה) כבר IDLE_MS. ובחזרה ללשונית/לאפליקציה (visible) — מיד,
// באותם תנאים, כי שם עוד לא התחילו לעבוד. קיוסק עם שדה סריקה בפוקוס לא מתרענן לבד.
export const IDLE_MS = 120_000;
export const TYPED_MS = 10 * 60_000;

/** בטוח לרענן עכשיו? אין דיאלוג פתוח ואין שדה עריכה בפוקוס. */
export function safeToReload(doc: Document = document): boolean {
  if (doc.querySelector('[role="dialog"], [role="alertdialog"], [aria-modal="true"], dialog[open]')) return false;
  const a = doc.activeElement as HTMLElement | null;
  if (a && (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName))) return false;
  return true;
}

export function StaleBuildNotice(): JSX.Element | null {
  const [serverSha, setServerSha] = useState<string | null>(null);
  const [serverTime, setServerTime] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  // הבאנר יושב מעל עשרה מסכים מתורגמים, כולל קיוסקים של הרצפה — שם דווקא
  // יושב מי שאינו קורא עברית. ראו edal-i18n-child-components.
  const { t, dir } = useI18n();
  const stale = isStale(serverSha, serverTime);

  // רענון אוטומטי בטוח (ראו למעלה).
  useEffect(() => {
    if (!stale) return;
    let last = Date.now();
    // הקלדה בטופס שלא נשלח (גם אם יצאו מהשדה / מהאפליקציה) — לא מרעננים עליה TYPED_MS.
    let typed = 0;
    const touch = (): void => { last = Date.now(); };
    const onInput = (): void => { last = Date.now(); typed = Date.now(); };
    const tryReload = (): void => {
      if (Date.now() - typed < TYPED_MS) return;
      if (safeToReload()) window.location.reload();
    };
    const onVisible = (): void => { if (document.visibilityState === 'visible') tryReload(); };
    const evs = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    evs.forEach((e) => window.addEventListener(e, touch, { passive: true, capture: true }));
    window.addEventListener('input', onInput, { capture: true });
    window.addEventListener('change', onInput, { capture: true });
    document.addEventListener('visibilitychange', onVisible);
    const id = setInterval(() => { if (Date.now() - last >= IDLE_MS) tryReload(); }, 15_000);
    return () => {
      evs.forEach((e) => window.removeEventListener(e, touch, { capture: true }));
      window.removeEventListener('input', onInput, { capture: true });
      window.removeEventListener('change', onInput, { capture: true });
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(id);
    };
  }, [stale]);

  useEffect(() => {
    let alive = true;

    const check = async (): Promise<void> => {
      try {
        const res = await fetch('/api/health', { credentials: 'include' });
        if (!res.ok && res.status !== 503) return;
        // 503 still carries a body: the server is up but the schema is behind,
        // which is exactly a moment when knowing the build matters.
        const body = (await res.json()) as { build?: { sha?: string; time?: string } };
        if (alive && body.build?.sha) {
          setServerSha(body.build.sha);
          setServerTime(body.build.time ?? null);
        }
      } catch {
        // Offline, or the server is restarting mid-deploy. Not worth a message:
        // the next tick answers, and a network blip must not produce a scary
        // banner on the shop floor.
      }
    };

    void check();
    const id = setInterval(() => void check(), POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (dismissed || !stale) return null;

  return (
    <div
      role="status"
      className="flex items-center justify-center gap-3 flex-wrap bg-amber-50 border-b border-amber-300 px-4 py-2 text-sm text-amber-900 print:hidden"
      dir={dir}
    >
      <RefreshCw className="w-4 h-4 flex-shrink-0" />
      <span className="font-semibold">{t('build.newVersion')}</span>
      <span className="text-amber-800">
        {t('build.stillOld')}
        {/* U+200E so the two shas do not swap places inside the RTL line. */}
        <span dir="ltr"> ({BUILD_SHA} → {serverSha}) </span>
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="bg-navy-600 hover:bg-navy-700 text-white font-bold px-3 py-1 rounded-lg"
      >
        {t('build.reloadNow')}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-amber-700 hover:text-amber-900 underline"
      >
        {t('build.notNow')}
      </button>
    </div>
  );
}
